import type { Tone } from "../../ui/StatusPill";
import { SalesOrder } from "../../common/requests/sales/endpoints";
import { OrderGroup, SalesFilterState, filtersToParams, flattenOrders, groupByOrder, statusRank } from "./salesData";

/* Pure helpers for the Sales page's table and order dialog. */

/** StatusPill tone for an order item status. */
/** Each order stage gets its own solid colour. */
export function statusTone(status: string): Tone {
  if (status === "Completed") return "success";
  if (status === "In Delivery") return "info";
  if (status === "Pending Delivery") return "ink";
  if (status === "Refunded" || status === "Partially Refunded" || status === "Cancelled") return "danger";
  return "neutral";
}

export type OrderSortKey = "placed" | "total" | "status";
export type SortDir = "asc" | "desc";

export const ORDER_SORTS: { value: string; label: string; key: OrderSortKey; dir: SortDir }[] = [
  { value: "placed-desc", label: "Newest first", key: "placed", dir: "desc" },
  { value: "placed-asc", label: "Oldest first", key: "placed", dir: "asc" },
  { value: "total-desc", label: "Highest total", key: "total", dir: "desc" },
  { value: "total-asc", label: "Lowest total", key: "total", dir: "asc" },
  { value: "status-asc", label: "Status: earliest stage", key: "status", dir: "asc" },
  { value: "status-desc", label: "Status: latest stage", key: "status", dir: "desc" },
];

/** Same ordering the old table used: by placed time, reporting total or the
 * order's earliest status. */
export function sortGroups(groups: OrderGroup[], key: OrderSortKey, dir: SortDir): OrderGroup[] {
  const sign = dir === "asc" ? 1 : -1;
  return groups.slice().sort((a, b) => {
    if (key === "total") return sign * ((a.netReporting ?? -1) - (b.netReporting ?? -1));
    if (key === "status") return sign * (statusRank(a.statuses[0]) - statusRank(b.statuses[0]));
    return sign * (a.createdAt.getTime() - b.createdAt.getTime());
  });
}

/** The whole order (every item, ignoring page filters) for the dialog.
 * `undefined` = not in the loaded data. `group` is null for an order with no items. */
export function findOrder(
  orders: SalesOrder[],
  id: string | null,
  now = new Date()
): { order: SalesOrder; group: OrderGroup | null } | undefined {
  const wanted = id?.trim();
  if (!wanted) return undefined;
  const order = orders.find((o) => o.order_id.trim() === wanted);
  if (!order) return undefined;
  const [group] = groupByOrder(flattenOrders([order]), now);
  return { order, group: group ?? null };
}

const FILTER_KEYS = ["range", "from", "to", "statuses", "sellers", "buyers", "currencies", "countries", "genera", "q", "attention"];

/** Writes the filters into the URL while keeping every other param
 * (`order`, `create`, ...) as it was. */
export function mergeFilterParams(prev: URLSearchParams, state: SalesFilterState): URLSearchParams {
  const next = new URLSearchParams(prev);
  FILTER_KEYS.forEach((k) => next.delete(k));
  filtersToParams(state).forEach((value, key) => next.set(key, value));
  return next;
}
